'use client'

import {
  codingImgUploadToStorage,
  preSignUrl,
  deleteUploadedCodingImg,
} from '@/apis/create'
import { ProfileCopyIcon } from '@/components/svg/ProfileCopyIcon'
import { useMessage } from '@/provider/MessageProvider'
import img from '@/icons/img.png'
import useI18n from 'Hooks/useI18n'
import { CreateExitMobileIcon } from '@/icons'
import { PlusAddIcon } from 'Comps/svg/PlusAddIcon'
import { Modal } from 'antd'
import clsx from 'clsx'
import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { CloseIcon } from '@/components/svg'

export type ImageLinkRecord = {
  key: string
  url: string
}

const MAX_RECORDS = 10
const MAX_UPLOAD_SIZE = 10 * 1024 * 1024

type NoticeType = 'fileTooLarge' | 'recordLimit' | null

type ImageToLinkModalProps = {
  open: boolean
  onClose: () => void
  scene: string
  records: ImageLinkRecord[]
  setRecords: React.Dispatch<React.SetStateAction<ImageLinkRecord[]>>
}

const getStorageKey = (scene: string) => `tipsy-image-to-link-records:${scene}`

const readClipboardFallback = async (text: string) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', 'true')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

export default function ImageToLinkModal({
  open,
  onClose,
  scene,
  records,
  setRecords,
}: ImageToLinkModalProps) {
  const { t } = useI18n()
  const messageApi = useMessage()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const rawValue = window.localStorage.getItem(getStorageKey(scene))
      if (!rawValue) return
      const parsed = JSON.parse(rawValue) as ImageLinkRecord[]
      if (Array.isArray(parsed)) {
        setRecords(parsed.slice(0, MAX_RECORDS))
      }
    } catch (error) {
      console.error('Failed to parse image-to-link records', error)
    }
  }, [scene, setRecords])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(
      getStorageKey(scene),
      JSON.stringify(records.slice(0, MAX_RECORDS))
    )
  }, [records, scene])

  const showUploadCard = records.length < MAX_RECORDS

  const placeholderCount = useMemo(
    () => Math.max(0, MAX_RECORDS - records.length - (showUploadCard ? 1 : 0)),
    [records.length, showUploadCard]
  )

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleOpenUpload = useCallback(() => {
    if (isUploading) return
    if (records.length >= MAX_RECORDS) {
      return
    }
    fileInputRef.current?.click()
  }, [isUploading, records.length])

  const handleUpload = useCallback(
    async (file: File | null) => {
      if (!file) return
      if (records.length >= MAX_RECORDS) {
        resetInput()
        return
      }
      if (file.size > MAX_UPLOAD_SIZE) {
        resetInput()
        messageApi.error(t('File is too large. Maximum size is 10MB.'))
        return
      }

      setIsUploading(true)
      try {
        const presignRes = await preSignUrl(
          scene,
          file.name,
          file.type,
          file.size
        )
        if (!presignRes || !presignRes.upload_url || !presignRes.object_key) {
          throw new Error('Failed to get presigned URL')
        }

        const uploadToOSS = await fetch(presignRes.upload_url, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file,
        })

        if (!uploadToOSS.ok) {
          throw new Error('Upload returned empty response')
        }
        const res = await codingImgUploadToStorage(presignRes.object_key)
        if (!res) {
          throw new Error('Failed to confirm image upload')
        }

        setRecords((prev) =>
          [
            { key: presignRes.object_key, url: presignRes.cdn_url },
            ...prev,
          ].slice(0, MAX_RECORDS)
        )
        messageApi.success(t('Image uploaded successfully.'))
      } catch (error) {
        console.error('Failed to upload image', error)
        messageApi.error(t('Upload failed. Please try again.'))
      } finally {
        setIsUploading(false)
        resetInput()
      }
    },
    [messageApi, records.length, scene, t, setRecords]
  )

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    handleUpload(file)
  }

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (records.length >= MAX_RECORDS) return
    setIsDragOver(true)
  }

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setIsDragOver(false)
    const file = event.dataTransfer.files?.[0] || null
    handleUpload(file)
  }

  const handleCopy = useCallback(
    async (url: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url)
        } else {
          await readClipboardFallback(url)
        }
        messageApi.success(t('Copied successfully.'))
      } catch (error) {
        console.error('Failed to copy url', error)
        messageApi.error(t('Copy failed. Please try again.'))
      }
    },
    [messageApi, t]
  )

  const handleDelete = useCallback(
    async (index: number) => {
      const record = records[index]
      if (!record) return
      try {
        setRecords((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
        await deleteUploadedCodingImg(record.key)
      } catch (error) {
        console.error('Failed to delete image', error)
      }
    },
    [records, setRecords]
  )

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      centered
      destroyOnClose={false}
      closeIcon={<CreateExitMobileIcon />}
      styles={{
        content: {
          padding: 0,
          borderRadius: '20px',
          background: 'rgba(42, 33, 36, 0.95)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)',
        },
        body: {
          padding: 0,
        },
      }}
    >
      <div className="px-7 pb-7 pt-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-[14px] font-normal leading-[14px] text-white">
              {t('Image to link')} :
            </div>
            <div className="mt-2 text-[10px] leading-[14px] text-white/40">
              {t('Up to 10 images')}
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
          className="hidden"
          onChange={handleFileChange}
        />

        <div className="grid grid-cols-5 gap-6">
          {showUploadCard && (
            <button
              type="button"
              onClick={handleOpenUpload}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={clsx(
                'relative flex h-[200px] cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed bg-white/[0.04] px-5 text-center transition',
                isDragOver
                  ? 'border-[#FFA133] bg-white/[0.08]'
                  : 'border-white/15 hover:border-white/30',
                isUploading && 'cursor-wait opacity-80'
              )}
            >
              <div className="mt-12">
                <PlusAddIcon size={24} color="#F5F5F5" />
              </div>
              <div className="mt-4 text-[14px] font-normal leading-[14px] text-white">
                {isUploading ? t('Uploading…') : t('Click or drag to upload')}
              </div>
              <div className="mt-2 text-center text-[10px] leading-[14px] text-white/40">
                {t('SVG, PNG or JPG')}
                <br />
                {t('(Max 10MB)')}
              </div>
            </button>
          )}

          {records.map((record, index) => (
            <div
              key={record.key}
              className="group relative h-[200px] overflow-hidden rounded-lg bg-white/[0.06]"
            >
              <img
                src={record.url}
                alt={t('Image')}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/10" />
              <button
                type="button"
                onClick={() => handleDelete(index)}
                className="absolute right-2 top-2 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-black/40 transition-opacity hover:opacity-80"
                aria-label={t('Delete')}
              >
                <CloseIcon size={12} />
              </button>
              <div className="absolute inset-x-0 bottom-0 z-10 p-2.5">
                <button
                  type="button"
                  onClick={() => handleCopy(record.url)}
                  className="flex h-7 w-7 shrink-0 items-center justify-center"
                  aria-label={t('Copy')}
                >
                  <div className=" absolute inset-x-0 bottom-0 z-10 flex items-center gap-2  p-2.5 px-3 py-2">
                    <span className="min-w-0 flex-1 truncate text-[12px] leading-4 text-white">
                      {record.url}
                    </span>
                    <ProfileCopyIcon size={16} />
                  </div>
                </button>
              </div>
            </div>
          ))}

          {Array.from({ length: placeholderCount }).map((_, index) => (
            <div
              key={`placeholder-${index}`}
              className="flex h-[200px] items-center justify-center rounded-lg bg-white/[0.04]"
            >
              <img
                src={img.src}
                alt={t('Placeholder')}
                width={40}
                height={40}
                className="opacity-30"
              />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}
